import { useEffect, useRef } from "react";
import { useParams } from "react-router";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { SURVEY_ELIGIBLE_ORDER_STATUSES } from "@jti/commerce/shared";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { BrowserStorage } from "@jti/rest-api";
import { useDispatch } from "@jti/store/reduxHooks";
import {
  fetchContactEmail,
  fetchOrderPoints,
  fetchPaymentMethod,
  resetOrderNumber,
} from "@jti/store/slices/LEGACY__orderSlice";
import { ConsentReminderDialog, SobQuiz, Survey, useTranslations } from "@jti/ui";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";

import { OrderSummaryRedirectToOrder } from "@checkout/components/OrderSummary/OrderSummaryRedirectToOrder";
import { SuccessfulPaymentMessage } from "@checkout/components/SuccessfulPaymentMessage";
import { useGtm } from "@checkout/hooks/gtm/useGtm";
import { useConsentPopUp } from "@checkout/hooks/useConsentPopUp";
import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import { useOrder } from "@checkout/hooks/useOrder";

import { AdditionalInfo } from "./components/AdditionalInfo/AdditionalInfo";
import { OrderLoyaltyConfirmation } from "./components/OrderLoyaltyConfirmation/";
import { OrderSummaryPageWarningMessage } from "./components/OrderSummaryPageWarningMessage";
import { OrderView } from "./components/OrderView/OrderView";
import { SocialMedia } from "./components/SocialMedia/SocialMedia";

import styles from "./OrderSummary.module.scss";

const { sanitize } = dompurify;

export const OrderSummaryPage = () => {
  const { showQuizOnThankYouPage } = useConfig();
  const resolveAuthHref = useAuthHrefResolver();
  const authEntryHref = resolveAuthHref({ route: AUTH_ROUTE_PATHS.entry });
  const dispatch = useDispatch();
  const { dispatchGtmOrderSummaryPurchase, updateGtmPageName } = useGtm();
  const { orderId, paymentToken } = useParams<{ orderId: string; paymentToken: string }>();
  const { __ } = useTranslations();
  const { survey, sob, consentReminderPopup, orderSummaryConfig } = useCheckoutConfiguration();
  const { isFreeTrial } = useFreeTrial();

  const { order, fetchOrder, orderFetchStatus, hasLoyalty, pending_points, points } = useOrder();

  const { isConsentPopUpVisible, closeConsentsPopUp } = useConsentPopUp();

  const gtmPurchaseDispatched = useRef(false);

  const isOrderEligibleForSurvey =
    orderFetchStatus === "success" &&
    !!order?.number &&
    !!order?.status &&
    SURVEY_ELIGIBLE_ORDER_STATUSES.includes(order.status);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (orderId) {
      fetchOrder(orderId);
      dispatch(fetchContactEmail());
      if (paymentToken) {
        dispatch(fetchPaymentMethod(paymentToken));
      }
    }
  }, [dispatch, fetchOrder, orderId, paymentToken]);

  useEffect(() => {
    if (orderId && hasLoyalty) {
      dispatch(fetchOrderPoints(orderId));
    }
  }, [orderId, hasLoyalty, dispatch]);

  useEffect(() => {
    if (!new BrowserStorage().get("user", null)) {
      window.location.href = authEntryHref;
    }

    // GTM Page Info Update
    const templateType = isFreeTrial ? "free-trial-order-confirmation-page" : "order-confirmation";
    updateGtmPageName(templateType);
  }, [authEntryHref, isFreeTrial, updateGtmPageName]);

  useEffect(() => {
    if (orderFetchStatus === "success" && order && !gtmPurchaseDispatched.current) {
      dispatchGtmOrderSummaryPurchase(order);

      gtmPurchaseDispatched.current = true;
    }

    return () => {
      dispatch(resetOrderNumber());
    };
  }, [orderFetchStatus, order, dispatchGtmOrderSummaryPurchase, dispatch]);

  return (
    <>
      <ConsentReminderDialog
        title={consentReminderPopup?.mainQuestion || ""}
        subtitle={consentReminderPopup?.subtitle || ""}
        description={consentReminderPopup?.description || ""}
        primaryButtonText={consentReminderPopup?.yesButtonText || ""}
        secondaryButtonText={consentReminderPopup?.noButtonText || ""}
        mainImage={consentReminderPopup?.mainImage || ""}
        imageAltText={consentReminderPopup?.imageAltText || ""}
        onPrimaryButtonClick={() => {
          consentReminderPopup?.yesRedirectUrl && window.open(consentReminderPopup.yesRedirectUrl, "_blank");
          closeConsentsPopUp();
        }}
        onSecondaryButtonClick={() => {
          closeConsentsPopUp();
        }}
        isOpened={isConsentPopUpVisible}
      />
      <div
        className={classNames(styles.container, {
          [styles.containerWithQuiz]: showQuizOnThankYouPage,
        })}
      >
        <SuccessfulPaymentMessage />
        <OrderSummaryRedirectToOrder orderId={order?.number} isFreeTrial={isFreeTrial} />

        {showQuizOnThankYouPage && isOrderEligibleForSurvey && survey && <Survey scriptUrl={survey.scriptUrl} />}
        {showQuizOnThankYouPage && isOrderEligibleForSurvey && !survey && sob?.data && <SobQuiz data={sob.data} />}

        <OrderView />
        <OrderLoyaltyConfirmation
          isActive={hasLoyalty && !isFreeTrial}
          pendingPoints={pending_points}
          totalPoints={points}
        />
        <OrderSummaryPageWarningMessage className={styles.trialMessage} isFreeTrial={isFreeTrial} />
        <AdditionalInfo />
        {!!orderSummaryConfig?.withdrawOrderInfo && (
          <div
            className={styles.withdrawOrderInfo}
            dangerouslySetInnerHTML={{ __html: sanitize(orderSummaryConfig.withdrawOrderInfo) }}
          />
        )}
        <SocialMedia />
      </div>
    </>
  );
};
